import {
  Calendar,
  ChevronDown,
  FileText,
  Folder,
  HelpCircle,
  Inbox,
  LogOut,
  Mail,
  Menu,
  Search,
  Send,
  Star,
} from "lucide-react";
import { useEffect, useState } from "react";
import type {
  InbasketEvaluation,
  MailReponses,
  VariablesData,
} from "../../lib/types";
import { MailStatus } from "../../lib/types";
import { cn } from "@repo/utils";
import { useCandidateAuthStore, useCandidateLogout } from "@repo/auth";
import { useUIStore } from "../../stores/uiStore";
import { getTemplate } from "../../templates";
import { getMailsComponentsForTemplate, getVisibleMails } from "../contenu-mails";
import type { InbasketTemplateConfig } from "../../templates/types";
import { Agenda, Budget, Informations, Organigramme } from "../documents";
import {
  Avatar,
  AvatarFallback,
  Button,
  DocumentModal,
  Input,
  ScrollArea,
  Separator,
} from "@repo/ui";
import { TutorialModal } from "../tutorial-modal";

// Props de CandidateSidebar
interface CandidateSidebarProps {
  evaluation: InbasketEvaluation;
  variablesData: VariablesData;
  template: InbasketTemplateConfig;
}

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  count?: number;
  filter: "all" | "important" | "sent" | "drafts";
}

function NavItem({ icon, label, count, filter }: NavItemProps) {
  const mailFilter = useUIStore((state) => state.mailFilter);
  const setMailFilter = useUIStore((state) => state.setMailFilter);

  const isActive = mailFilter === filter;

  const handleClick = () => {
    setMailFilter(filter);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-controle px-3 py-2 text-sm font-medium transition-colors",
        isActive
          ? "bg-sidebar-primary/10 text-sidebar-primary font-semibold"
          : "text-sidebar-foreground hover:bg-sidebar-accent/50",
      )}
    >
      {icon}
      <span className="flex-1 text-left">{label}</span>
      {count !== undefined && count > 0 && (
        <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">
          {count}
        </span>
      )}
    </button>
  );
}

// Types de documents affichés dans la barre latérale
type DocumentItem = {
  id: string;
  titre: string;
  Component: React.ComponentType<{ variablesData: VariablesData }>;
};

// Déclare les composants de documents
const documentItems: DocumentItem[] = [
  {
    id: "organigramme",
    titre: "Organigramme",
    Component: Organigramme,
  },
  {
    id: "informations",
    titre: "Informations entreprise",
    Component: Informations,
  },
  { id: "budget", titre: "Budget annuel", Component: Budget },
];

function DocumentsSection({ variablesData }: { variablesData: VariablesData }) {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<DocumentItem | null>(null);

  return (
    <>
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full items-center gap-2 rounded-controle px-3 py-2 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
        >
          <Folder className="h-4 w-4" />
          <span className="flex-1 text-left">Documents</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              isOpen ? "rotate-0" : "-rotate-90",
            )}
          />
        </button>
        {isOpen && (
          <div className="ml-4 mt-1 space-y-1">
            {documentItems.map((doc) => (
              <button
                key={doc.id}
                onClick={() => setSelectedDoc(doc)}
                className="flex w-full items-center gap-3 rounded-controle px-3 py-2 text-sm font-medium text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
              >
                <FileText className="h-4 w-4" />
                <span>{doc.titre}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <DocumentModal
        isOpen={selectedDoc !== null}
        onClose={() => setSelectedDoc(null)}
        title={selectedDoc?.titre}
      >
        {selectedDoc && <selectedDoc.Component variablesData={variablesData} />}
      </DocumentModal>
    </>
  );
}

function AgendaSection({ variablesData }: { variablesData: VariablesData }) {
  const [isAgendaOpen, setIsAgendaOpen] = useState(false);

  return (
    <>
      <div className="mt-2">
        <button
          onClick={() => setIsAgendaOpen(true)}
          className="flex w-full items-center gap-3 rounded-controle px-3 py-2 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
        >
          <Calendar className="h-4 w-4" />
          <span>Agenda</span>
        </button>
      </div>

      <DocumentModal
        isOpen={isAgendaOpen}
        onClose={() => setIsAgendaOpen(false)}
      >
        <Agenda variablesData={variablesData} />
      </DocumentModal>
    </>
  );
}

function TutorialButton() {
  const openTutorial = useUIStore((state) => state.openTutorial);

  return (
    <button
      type="button"
      onClick={openTutorial}
      className="flex w-full items-center gap-3 rounded-controle px-3 py-2 text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
    >
      <HelpCircle className="h-4 w-4" />
      <span>Relancer le tutoriel</span>
    </button>
  );
}

function CandidateSidebar({
  evaluation,
  variablesData,
  template,
}: CandidateSidebarProps) {
  const calculateCounts = () => {
    if (!evaluation) {
      return {
        inbox: 0,
        unread: 0,
        answered: 0,
        pending: 0,
        important: 0,
        drafts: 0,
      };
    }

    const mailsReponses = evaluation.donnees.reponses as MailReponses;
    const displayedMails = getVisibleMails(
      getMailsComponentsForTemplate(template),
      mailsReponses,
      template.mailTypeReponses,
    ).map((m) => m.metadata);

    const unread = displayedMails.filter((mail) => {
      const response = mailsReponses[mail.cleMail];
      return !response || response.statut === MailStatus.NON_LU;
    }).length;

    const answered = displayedMails.filter((mail) => {
      const response = mailsReponses[mail.cleMail];
      return response?.statut === MailStatus.REPONDU;
    }).length;

    const drafts = displayedMails.filter((mail) => {
      const response = mailsReponses[mail.cleMail];
      return response?.estEnBrouillon === true;
    }).length;

    const important = displayedMails.filter((mail) => {
      const response = mailsReponses[mail.cleMail];
      return response?.important === true;
    }).length;

    const pending = displayedMails.filter((mail) => {
      if (template.mailTypeReponses[mail.cleMail] === "no_response") return false;
      const response = mailsReponses[mail.cleMail];
      if (!response) return false;
      return (
        response.statut !== MailStatus.REPONDU &&
        response.statut !== MailStatus.NON_LU
      );
    }).length;

    return {
      inbox: displayedMails.length,
      unread,
      answered,
      pending,
      important,
      drafts,
    };
  };

  const counts = calculateCounts();

  return (
    <aside className="flex h-full w-64 flex-col border-r border-sidebar-border bg-sidebar">
      <div className="flex h-14 items-center gap-2 border-b border-sidebar-border px-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-controle bg-primary">
          <Mail className="h-4 w-4 text-primary-foreground" />
        </div>
        <span className="text-lg font-semibold text-sidebar-foreground">
          InBasket
        </span>
      </div>

      {/* Padding à l'intérieur de la zone défilante, pas sur elle : le
          viewport du ScrollArea recadre au bord, et rognait le contour de
          focus des boutons, qui en occupent toute la largeur. */}
      <ScrollArea className="flex-1">
        <div className="px-3 py-4">
          <nav className="space-y-1">
            <NavItem
              filter="all"
              icon={<Inbox className="h-4 w-4" />}
              label="Boite de reception"
              count={counts.unread}
            />
            <NavItem
              filter="important"
              icon={<Star className="h-4 w-4" />}
              label="Importants"
              count={counts.important}
            />
            <NavItem
              filter="sent"
              icon={<Send className="h-4 w-4" />}
              label="Envoyes"
              count={counts.answered}
            />
            <NavItem
              filter="drafts"
              icon={<FileText className="h-4 w-4" />}
              label="Brouillons"
              count={counts.drafts}
            />
          </nav>

          <Separator className="my-4" />
          <AgendaSection variablesData={variablesData} />
          <DocumentsSection variablesData={variablesData} />

          <Separator className="my-4" />

          <div className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/60">
              Statistiques
            </h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-sidebar-foreground/80">Total</span>
                <span className="font-medium text-sidebar-foreground">
                  {counts.inbox}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-sidebar-foreground/80">Non lus</span>
                <span className="font-medium text-sidebar-foreground">
                  {counts.unread}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-sidebar-foreground/80">Repondus</span>
                <span className="font-medium text-sidebar-foreground">
                  {counts.answered}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-sidebar-foreground/80">En attente</span>
                <span className="font-medium text-sidebar-foreground">
                  {counts.pending}
                </span>
              </div>
            </div>
          </div>

          <Separator className="my-4" />
          <TutorialButton />
        </div>
      </ScrollArea>
    </aside>
  );
}

interface CandidateNavbarProps {
  dateDebut?: number;
  dureeConseillee: number;
}

function CandidateNavbar({ dateDebut, dureeConseillee }: CandidateNavbarProps) {
  const firstName = useCandidateAuthStore((state) => state.firstName);
  const token = useCandidateAuthStore((state) => state.token);
  const { logout } = useCandidateLogout();
  const toggleSidenav = useUIStore((state) => state.toggleSidenav);
  const searchQuery = useUIStore((state) => state.searchQuery);
  const setSearchQuery = useUIStore((state) => state.setSearchQuery);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  const fullName = `${firstName} ${token}`.trim();
  const initials =
    `${firstName.charAt(0)}${token.charAt(0)}`.toUpperCase() || "CA";

  const heureFinConseillee = dateDebut
    ? new Date(dateDebut + dureeConseillee * 60_000).toLocaleTimeString(
        "fr-FR",
        { hour: "2-digit", minute: "2-digit" },
      )
    : null;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(localSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearchQuery, setSearchQuery]);

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-card px-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={toggleSidenav}
        >
          <Menu className="h-5 w-5" />
        </Button>

        <div className="relative hidden sm:block">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Rechercher des emails..."
            className="w-64 pl-9 md:w-80"
            value={localSearchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setLocalSearchQuery(e.target.value)
            }
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="hidden items-center gap-3 sm:flex">
          <div className="text-right">
            <p className="text-sm font-medium text-foreground">
              {fullName || "Candidat"}
            </p>
            {heureFinConseillee ? (
              <p className="text-xs text-muted-foreground">
                Fin conseillée : {heureFinConseillee}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Evaluation en cours
              </p>
            )}
          </div>
          <Avatar className="h-8 w-8 bg-primary">
            <AvatarFallback className="bg-primary text-xs text-primary-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>
        <Button variant="ghost" size="sm" onClick={logout} className="gap-2">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}

// Props de Layout, avec render props
interface LayoutProps {
  evaluationId: string;
  token: string;
  evaluation: InbasketEvaluation;
  children: (props: {
    evaluationId: string;
    token: string;
    evaluation: InbasketEvaluation;
  }) => React.ReactNode;
}

export function Layout({
  evaluationId,
  token,
  evaluation,
  children,
}: LayoutProps) {
  const displaySidenav = useUIStore((state) => state.displaySidenav);
  const setSidenav = useUIStore((state) => state.setSidenav);
  const isTutorialOpen = useUIStore((state) => state.isTutorialOpen);
  const closeTutorial = useUIStore((state) => state.closeTutorial);

  // Récupère variablesData depuis le template
  const template = getTemplate(evaluation.donnees.template);
  const variablesData = template?.variablesData;

  return (
    <div className="relative flex h-screen w-screen overflow-hidden bg-background">
      {isTutorialOpen && (
        <TutorialModal variablesData={variablesData} onClose={closeTutorial} />
      )}
      {displaySidenav && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidenav(false)}
        />
      )}

      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0",
          displaySidenav ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <CandidateSidebar
          evaluation={evaluation}
          variablesData={variablesData}
          template={template}
        />
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        <CandidateNavbar
          dateDebut={evaluation.dateDebut}
          dureeConseillee={template.dureeConseillee}
        />
        <main className="flex flex-1 overflow-hidden">
          {children({ evaluationId, token, evaluation })}
        </main>
      </div>
    </div>
  );
}
